import { Box, IconButton, TableCell, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { CustomTableCellProps, WrapperProps } from './types';

export const Wrapper = styled(Box)<WrapperProps>(({ isshortwidth, theme }) => ({
  borderRadius: '4px',
  height: 'max-content',
  border: '1px solid #DDDDEA',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: isshortwidth ? 'calc(100% - 360px)' : undefined,

  '&>.tableContainer::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },

  '&>.tableContainer::-webkit-scrollbar-track': {
    backgroundColor: 'rgba(30, 237, 159, 0.1)',
    borderRadius: '4px',
  },

  '&>.tableContainer::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(30, 237, 159, 0.6)',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(30, 237, 159, 0.8)',
    },
  },

  [theme.breakpoints.down('lg')]: {
    maxWidth: isshortwidth ? 'calc(100% - 255px)' : undefined,
  },

  [theme.breakpoints.down('sm')]: {
    maxWidth: 'none',
  },
}));

export const CustomHeadTableCell = styled(TableCell)<CustomTableCellProps>(
  ({ isborderright, theme }) => ({
    position: 'relative',
    borderRight: isborderright ? '1px solid #DDDDEA' : undefined,
    padding: '10px',
    width: '150px',
    // Importante: lo sticky header deve essere opaco, altrimenti durante lo scroll
    // si vede "sotto" il contenuto delle righe (es. il valore precedente tra parentesi).
    background: theme.palette.grey[200],
    zIndex: 2,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'grab',

    // Effetto di sollevamento al hover
    '&:hover': {
      background: theme.palette.action.selected,
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 10,
    },

    // Stato di dragging
    '&.dragging': {
      opacity: 0.5,
      transform: 'rotate(5deg) scale(1.05)',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.25)',
      zIndex: 1000,
      cursor: 'grabbing',
    },

    // Drop zone attiva - rimossa perché colorava tutte le colonne

    // Drop zone hover - solo la colonna dove può atterrare
    '&.drop-zone-hover': {
      background: `linear-gradient(45deg, ${theme.palette.primary.main}40, ${theme.palette.primary.main}80) !important`,
      color: theme.palette.primary.main,
      transform: 'translateY(-3px) scale(1.02)',
      boxShadow: `0 8px 25px ${theme.palette.primary.main}80, 0 0 0 2px ${theme.palette.primary.main}40`,
      border: `3px dashed ${theme.palette.primary.main}`,
      borderRadius: '8px',
      animation: 'dropZonePulse 1s infinite',
      zIndex: 100,
      position: 'relative',
    },

    // Effetto sottile per tutte le colonne durante il drag
    '&.drag-active': {
      background: theme.palette.action.hover,
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
    },

    // Classe generale per il modo drag
    '&.drag-mode': {
      transition: 'all 0.15s ease',
    },

    '@keyframes dropZonePulse': {
      '0%, 100%': {
        transform: 'translateY(-3px) scale(1.02)',
        boxShadow: `0 8px 25px ${theme.palette.primary.main}80, 0 0 0 2px ${theme.palette.primary.main}40`,
      },
      '50%': {
        transform: 'translateY(-3px) scale(1.05)',
        boxShadow: `0 12px 35px ${theme.palette.primary.main}90, 0 0 0 4px ${theme.palette.primary.main}60`,
      },
    },

    '&.sorted': {
      background: theme.palette.action.hover,
      boxShadow: `inset 0 -2px 0 ${theme.palette.primary.main}`,
    },

    // Animazione di entrata per le nuove colonne
    '&.column-enter': {
      animation: 'columnSlideIn 0.3s ease-out',
    },

    '@keyframes columnSlideIn': {
      '0%': {
        opacity: 0,
        transform: 'translateX(-20px)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateX(0)',
      },
    },
  }),
);

export const CustomBodyTableCell = styled(TableCell)<CustomTableCellProps>(
  ({ isborderright }) => ({
    borderRight: isborderright ? '1px solid #DDDDEA' : undefined,
    borderBottom: 'none',
    padding: '7.5px 10px',
    transition: 'all 0.2s ease',

    // Effetto di sollevamento per le celle del body durante il drag
    '.dragging &': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
  }),
);

export const DragIndicatorWrapper = styled(Box)(({ theme }) => ({
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  borderRadius: '4px',
  transition: 'all 0.2s ease',
  cursor: 'grab',

  '&:hover': {
    background: theme.palette.action.hover,
    transform: 'scale(1.1)',
  },

  '&:active': {
    cursor: 'grabbing',
    transform: 'scale(0.95)',
  },

  // Effetto di rotazione durante il drag
  '.dragging &': {
    animation: 'dragPulse 1s infinite',
  },

  '@keyframes dragPulse': {
    '0%, 100%': {
      transform: 'rotate(0deg) scale(1)',
    },
    '50%': {
      transform: 'rotate(5deg) scale(1.05)',
    },
  },
}));

export const Title = styled(Typography)(() => ({
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '21px',
  transition: 'color 0.2s ease',

  '& > img': {
    height: '40px',
    width: '40px',
    objectFit: 'cover',
  },
}));

export const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  borderRadius: '5px',
  border: `0.5px solid ${theme.palette.grey[800]}`,
  height: '20px',
  width: '20px',
  transition: 'all 0.2s ease',

  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  },

  '&:hover svg': {
    opacity: '0.75',
    color: theme.palette.grey[800],
  },

  '& svg': {
    color: theme.palette.grey[800],
  },
}));

export const SortIconWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ active, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: '6px',
  fontSize: '1.1em',
  color: active ? theme.palette.primary.main : '#333',
  cursor: 'pointer',
  outline: 'none',
  transition: 'all 0.2s ease',

  '&:hover, &:focus': {
    color: theme.palette.primary.dark,
    background: 'rgba(25, 118, 210, 0.08)',
    borderRadius: '50%',
    transform: 'scale(1.1)',
  },
}));

// Nuovo componente per l'indicatore di drop zone
export const DropZoneIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `linear-gradient(45deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}40)`,
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  pointerEvents: 'none',
  zIndex: 5,

  '&.active': {
    opacity: 1,
    animation: 'dropZonePulse 1.5s infinite',
  },

  '&::before': {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  '@keyframes dropZonePulse': {
    '0%, 100%': {
      transform: 'scale(1)',
      boxShadow: `0 0 0 0 ${theme.palette.primary.main}40`,
    },
    '50%': {
      transform: 'scale(1.02)',
      boxShadow: `0 0 0 8px ${theme.palette.primary.main}20`,
    },
  },
}));

// Componente per l'effetto di ombra durante il drag
export const DragShadow = styled(Box)(({ theme }) => ({
  position: 'fixed',
  pointerEvents: 'none',
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: '8px 12px',
  borderRadius: '4px',
  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
  transform: 'rotate(5deg)',
  zIndex: 9999,
  opacity: 0.9,
  fontSize: '14px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
}));
